/* eslint-disable no-nested-ternary */
import React, { useCallback } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { space, flexbox, border, color, layout, position } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import Text from 'theme/text';
import StatusLabel from 'theme/status-label';
import { CircleFlag } from 'react-circle-flags';
import PaymentIcon from 'theme/payment-icon';
import translations from 'translations';
import ToolTip from 'theme/tooltip';

const Wrapper = styled.button`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 390px;
  min-height: 148px;
  box-sizing: border-box;
  border-radius: 8px;
  padding: 16px 16px 16px 32px;

  ${space}
  ${border}
  ${color}
  ${layout}

  & > .body {
    width: 100%;
    padding-right: 16px;
  }
`;

const Flex = styled.div`
  display: flex;

  ${flexbox}
  ${space}
  ${position}
`;

const StyledText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
  text-align: left;
`;

const StyledCircleFlag = styled(CircleFlag)`
  width: 22px;
  height: 22px;
`;

const Footer = styled.div`
  width: 100%;

  ${space}

  & > .divider {
    width: 100%;
    height: 1px;
    background-color: ${themeGet('colors.primary_100')};
    margin: ${themeGet('space.s')}px 0;
  }

  & > .section-status {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`;

const WrapStatus = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  ${space}
`;

const CircleStatus = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 24px;
  background-color: ${({ valid, isDraft }) =>
    !valid
      ? themeGet('colors.red_400_transparent')
      : isDraft
      ? themeGet('colors.grey_100')
      : themeGet('colors.primary_100')};
  display: flex;
  justify-content: center;
  align-items: center;

  ::after {
    content: '';
    width: 16px;
    height: 16px;
    border-radius: 16px;
    background-color: ${({ valid, isDraft }) =>
      !valid
        ? themeGet('colors.red_400')
        : isDraft
        ? themeGet('colors.grey_200')
        : themeGet('colors.primary_400')};
  }
`;

function BillingProfileCard({
  id,
  languageCode,
  style,
  label,
  subTitle,
  paymentAccount,
  countryCode,
  validForActiveInvoice,
  validForPassiveInvoice,
  extraInfo,
  isDraft,
  isDefault,
}) {
  const { push } = useRouter();

  const handleSummary = useCallback(() => {
    push({ pathname: '/billing/summary-billing-profile', query: { id } });
  }, [push, id]);

  return (
    <Wrapper bg="white" {...style} onClick={handleSummary}>
      <Flex
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        position="relative"
      >
        <Flex flexDirection="row" alignItems="center">
          {!isDraft && (
            <StatusLabel style={{ p: 0 }} languageCode={languageCode} status="COMPLETE" />
          )}
          {!!isDraft && <StatusLabel style={{ p: 0 }} languageCode={languageCode} status="DRAFT" />}

          {isDefault && (
            <StatusLabel
              style={{ margin: '0 0 0 10px' }}
              languageCode={languageCode}
              status="DEFAULT"
            />
          )}

          <Flex mx="s">
            <PaymentIcon paymentMethodCode={paymentAccount.paymentMethodCode} />
          </Flex>
          {!!countryCode && <StyledCircleFlag countryCode={countryCode.toLowerCase()} />}
        </Flex>
      </Flex>

      <div className="body">
        <StyledText mt="s" fontSize="h3" lineHeight="h3" fontWeight="bold" color="grey_600">
          {label}
        </StyledText>
        <StyledText my="xs" fontSize="h3" lineHeight="h3" fontWeight="medium" color="grey_400">
          {subTitle}
        </StyledText>
      </div>

      {extraInfo && (
        <Footer mr="s">
          <div className="divider" />

          <div className="section-status">
            <ToolTip
              forData={`active-${id}`}
              text={
                validForActiveInvoice
                  ? translations[languageCode]['tooltip.activeValid']
                  : translations[languageCode]['tooltip.activeNotValid']
              }
              arrowColor="#098EA0"
              backgroundColor="#098EA0"
              offset={{ top: -10 }}
              place="top"
              effect="solid"
              html
              multiline
            />

            <WrapStatus p="xs" data-tip data-for={`active-${id}`}>
              <CircleStatus valid={validForActiveInvoice} isDraft={false} />
              <Text
                pl="xs"
                fontSize="pSmall"
                lineHeight="pSmall"
                fontWeight="medium"
                color="grey_600"
              >
                {translations[languageCode].Active}
              </Text>
            </WrapStatus>

            <ToolTip
              forData={`passive-${id}`}
              text={
                validForPassiveInvoice
                  ? translations[languageCode]['tooltip.passiveValid']
                  : translations[languageCode]['tooltip.passiveNotValid']
              }
              arrowColor="#098EA0"
              backgroundColor="#098EA0"
              offset={{ top: -10 }}
              place="top"
              effect="solid"
              html
              multiline
            />

            <WrapStatus ml="s" p="xs" data-tip data-for={`passive-${id}`}>
              <CircleStatus valid={validForPassiveInvoice} isDraft={isDraft} />
              <Text
                pl="xs"
                fontSize="pSmall"
                lineHeight="pSmall"
                fontWeight="medium"
                color="grey_600"
              >
                {translations[languageCode].Passive}
              </Text>
            </WrapStatus>
          </div>
        </Footer>
      )}
    </Wrapper>
  );
}

export default BillingProfileCard;
